import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/ux-common/lib/utils';

/**
 * Popover — non-modal floating content anchored to a trigger, on
 * @radix-ui/react-popover (Escape and outside-click dismiss, focus moves in on
 * open and back to the trigger on close).
 *
 * `PopoverContent` ALWAYS renders in a portal and stacks above `--z-modal`. That
 * is the point of it: a registry Dialog clips anything rendered inside it —
 * `.dialog` is `overflow: clip`, `.dialog-body` is a scroll container, and the
 * dialog's `translate` makes it the containing block for `position: fixed`, so
 * not even a fixed element escapes. A popover opened from a Dialog therefore has
 * to leave the dialog's DOM, and then has to outrank it.
 *
 * It is a primitive with no visual design yet: the layer draws no surface,
 * border, radius or shadow. The content brings its own — CalendarPicker paints
 * `Calendar/bg/container`. A designed popover surface can land later as
 * `--popover-*` variables without changing this API.
 *
 * @example
 * ```tsx
 * <Popover open={open} onOpenChange={setOpen}>
 *   <PopoverTrigger asChild>
 *     <DropdownButton className="btn-tier3">{label}</DropdownButton>
 *   </PopoverTrigger>
 *   <PopoverContent>
 *     <CalendarPicker mode="single" defaultValue={date} onConfirm={commit} onCancel={close} />
 *   </PopoverContent>
 * </Popover>
 * ```
 */

export const Popover = PopoverPrimitive.Root;

export type PopoverTriggerProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>;

/**
 * PopoverTrigger — opens and closes the popover. Forwards its ref and stamps
 * `data-component`. Radix already renders `type="button"`, so a bare trigger inside
 * a form does not submit it. Under `asChild` the stamp lands on the child too and
 * replaces its own (a registry `Button` reads `PopoverTrigger`), as `TooltipTrigger`
 * does: it names what the element does here.
 */
export const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  PopoverTriggerProps
>((props, ref) => (
  <PopoverPrimitive.Trigger ref={ref} data-component="PopoverTrigger" {...props} />
));
PopoverTrigger.displayName = 'PopoverTrigger';

/** PopoverAnchor — positions the content against an element other than the trigger. */
export const PopoverAnchor = PopoverPrimitive.Anchor;

export type PopoverCloseProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Close>;

/**
 * PopoverClose — closes the popover; compose a button with `asChild`. Forwards its
 * ref and stamps `data-component`, like the trigger.
 */
export const PopoverClose = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Close>,
  PopoverCloseProps
>((props, ref) => <PopoverPrimitive.Close ref={ref} data-component="PopoverClose" {...props} />);
PopoverClose.displayName = 'PopoverClose';

export type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>;

/**
 * PopoverContent — the floating layer, portalled and opening 2px from its
 * trigger, aligned to the trigger's START edge. Radix flips it to the other side
 * when there is no room; it is capped at the available height and scrolls past that.
 *
 * `align` defaults to `'start'`, not Radix's `'center'`: a popover anchored to a
 * field or button lines up with its leading edge, as DropdownInput's menu does.
 * Pass `align="center"` or `"end"` to change it.
 */
export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, sideOffset = 2, align = 'start', ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      data-component="PopoverContent"
      sideOffset={sideOffset}
      className={cn('popover', className)}
      {...props}
    />
  </PopoverPrimitive.Portal>
));

PopoverContent.displayName = 'PopoverContent';
